if (!customElements.get("save-search-button")) {
  customElements.define(
    "save-search-button",
    class SaveSearchButton extends HTMLElement {
      connectedCallback() {
        this.init();
        this.interactiveDemo = document.querySelector('[data-target-id="fp-save-search-button"]');
      }

      disconnectedCallback() {
        this.interactiveDemo = null;
      }

      getRefinementListFromUrl(search = window.location.search) {
        const values = [];
        const params = new URLSearchParams(search);
        const entries = params.entries();
        for (const [key, value] of entries) {
          if (key.startsWith("refinementList")) {
            values.push(value);
          }
        }
        return values;
      }

      extractRefinementsFromUrl(data = []) {
        let result = [];
        for (const item of data) {
          const itemUrl = item?.url;
          if (!itemUrl) {
            continue;
          }
          const url = new URL(itemUrl);
          const refinements = this.getRefinementListFromUrl(url.search).sort();
          const urlQueryParams = new URLSearchParams(url.search);
          const query = urlQueryParams.get("q") || "";
          result.push({
            path: url.pathname,
            refinements,
            query,
          });
        }
        return result;
      }

      async checkIfCurrentSearchIsAlreadySaved() {
        const savedSearch = await this.getSavedSearch();
        const savedRefinements = this.extractRefinementsFromUrl(savedSearch);
        const currentRefinements = this.getRefinementListFromUrl().sort();
        const currentPath = window.location.pathname;
        const currentQueryParams = new URLSearchParams(window.location.search);
        const currentQuery = currentQueryParams.get("q") || "";

        const isAlreadySaved = savedRefinements.some(
          (saved) =>
            saved.path === currentPath &&
            JSON.stringify(saved.refinements) === JSON.stringify(currentRefinements) &&
            saved.query === currentQuery,
        );

        if (isAlreadySaved) {
          this.setButtons({ disabled: true, label: "Search Saved" });
        } else {
          this.setButtons({ disabled: false, label: "Save Search" });
        }
      }

      async getSavedSearch() {
        const savedSearches = [];
        let page = 1;
        const { data, pagination } = await fetchSavedSearches(page);
        if (!data) {
          return [];
        }
        if (pagination?.total_pages && (pagination?.total_pages || 0) > 1) {
          const promises = [];
          for (let p = 2; p <= pagination.total_pages; p++) {
            promises.push(fetchSavedSearches(p));
          }
          const results = await Promise.all(promises);
          results.forEach((result) => {
            if (result?.data) {
              savedSearches.push(...result.data);
            }
          });
        }
        savedSearches.push(...(data?.data || []));

        return savedSearches;
      }

      setButtons({ disabled, label }) {
        document.querySelectorAll(".fp-save-search-button").forEach((button) => {
          const copy = button.querySelector(".fp-save-search-copy");

          if (disabled) {
            button.setAttribute("disabled", "true");
            button.setAttribute("aria-disabled", "true");
            if (label) {
              button.setAttribute("aria-label", label);
              if (copy) {
                copy.textContent = label;
              }
            }
          } else {
            button.removeAttribute("disabled");
            button.removeAttribute("aria-disabled");
            if (label) {
              button.setAttribute("aria-label", label);
              if (copy) {
                copy.textContent = label;
              }
            }
          }
        });
      }

      setVisibility({ hidden = false }) {
        if (hidden) {
          this.setAttribute("hidden", "true");
          this.setAttribute("aria-hidden", "true");
        } else {
          this.removeAttribute("hidden");
          this.removeAttribute("aria-hidden");
        }
      }

      getCollectionfromUrl() {
        const pathname = window.location.pathname;
        const paths = pathname.split("/");
        const collectionsIndex = paths.indexOf("collections");
        if (collectionsIndex !== -1 && paths.length > collectionsIndex + 1) {
          return paths[collectionsIndex + 1];
        }
        return null;
      }

      async getCollectionByHandle(handle) {
        const query = `
          query($handle: String!) {
            collection(handle: $handle) {
              isSaveSearchEnabled: metafield(namespace: "custom", key: "enable_save_search") { value }
            }
          }
        `;
        const res = await fetch("/api/2023-07/graphql.json", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Shopify-Storefront-Access-Token": window.storefrontToken,
          },
          body: JSON.stringify({ query, variables: { handle } }),
        });
        const { data, errors } = await res.json();
        if (errors) {
          console.error(errors);
          return null;
        }
        return data.collection;
      }

      async setup() {
        if (!window.saveSearchListenerInitialized) {
          window.saveSearchListenerInitialized = true;

          await this.checkIfCurrentSearchIsAlreadySaved();
          let lastRefinements = this.getRefinementListFromUrl().sort();

          document.addEventListener("click", (event) => {
            // enable the save-search button when the filters are cleared
            if (event.target.className == "ais-ClearRefinements-button") {
              this.setButtons({ disabled: false, label: "Save Search" });
            }
            if (
              event.target.closest(".ais-RefinementList-checkbox") ||
              event.target.closest(".ais-CurrentRefinements-delete")
            ) {
              this.setButtons({ disabled: true, label: "Search Saved" });
              let attempts = 0;
              const maxAttempts = 5;
              const interval = setInterval(() => {
                const currentRefinements = this.getRefinementListFromUrl().sort();

                if (JSON.stringify(currentRefinements) !== JSON.stringify(lastRefinements)) {
                  this.checkIfCurrentSearchIsAlreadySaved();
                  lastRefinements = currentRefinements;
                  clearInterval(interval);
                }

                attempts++;
                if (attempts >= maxAttempts) {
                  clearInterval(interval);
                }
              }, 350);
            }
          });
        }
      }

      async init() {
        const collectionHandle = this.getCollectionfromUrl();
        let isSaveSearchEnabledVal = null;
        if (collectionHandle) {
          const collection = await this.getCollectionByHandle(collectionHandle);
          isSaveSearchEnabledVal = collection?.isSaveSearchEnabled?.value;
        }
        if (isSaveSearchEnabledVal === "false") {
          this.setVisibility({ hidden: true });
        } else {
          this.setVisibility({ hidden: false });
        }

        if (isLoggedIn) {
          await this.setup();
        }

        // NOTE:Determine whether we need to open the interactive demo after completing the setup method.
        if (
          isSaveSearchEnabledVal != "false" &&
          this.interactiveDemo &&
          !this.interactiveDemo.classList.contains("active")
        ) {
          this.interactiveDemo.open();
        }

        this.addEventListener("click", () => {
          if (!isLoggedIn || isLoggedIn === "false") {
            const loginUrl = getLoginUrl();
            window.location.replace(loginUrl);
            return;
          }
          const modal = document.getElementById("fp-save-search-modal");
          modal.style.setProperty("display", "block");
          modal.closest(".shopify-section").classList.remove("hidden--important");
        });
      }
    },
  );
}
